import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2, Save, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";

const LANGS = [
  { key: "ca", label: "Català" },
  { key: "es", label: "Español" },
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
];

const emptyMl = () => ({ ca: "", es: "", en: "", fr: "" });

/**
 * BlogEditor — full CRUD modal for a single blog post.
 * Props:
 *  - post: existing post or null (to create new one)
 *  - onClose(): callback
 *  - onSaved(): callback after successful save
 */
export function BlogEditor({ post, onClose, onSaved }) {
  const isNew = !post;
  const [slug, setSlug] = useState(post?.id || post?.slug || "");
  const [tag, setTag] = useState(post?.tag || "Tecnologia");
  const [cover, setCover] = useState(post?.cover || "");
  const [published, setPublished] = useState(post?.published ?? true);
  const [title, setTitle] = useState({ ...emptyMl(), ...(post?.title || {}) });
  const [excerpt, setExcerpt] = useState({ ...emptyMl(), ...(post?.excerpt || {}) });
  const [body, setBody] = useState({ ...emptyMl(), ...(post?.body || {}) });
  const [activeLang, setActiveLang] = useState("ca");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    // ESC to close
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const slugify = (s) => (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);

  const onTitleChange = (v) => {
    setTitle({ ...title, [activeLang]: v });
    if (isNew && !slug && activeLang === "ca") setSlug(slugify(v));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Màx 5 MB"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(",")[1];
      const { data } = await api.post("/uploads/image", {
        filename: file.name, content_type: file.type, data_base64: base64,
      });
      const absolute = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setCover(absolute);
      toast.success("Imatge pujada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No s'ha pogut pujar l'imatge");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!slug.trim()) return toast.error("Cal un slug (identificador únic).");
    if (!title.ca.trim()) return toast.error("Cal el títol en català com a mínim.");
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/blog", { slug: slug.trim(), tag, cover, title, excerpt, body, published });
        toast.success("Article creat");
      } else {
        await api.patch(`/blog/${post.id}`, { tag, cover, title, excerpt, body, published });
        toast.success("Canvis guardats");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardant");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-black border border-white/10 rounded-lg px-3.5 py-2.5 text-white placeholder:text-white/25 focus:outline-none focus:border-neon/60 text-sm";
  const labelCls = "font-mono text-[10px] uppercase tracking-widest text-white/40";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
         data-testid="blog-editor" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-4xl bg-[#0A0A0A] border border-white/10 rounded-2xl my-4">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur rounded-t-2xl">
          <h2 className="font-display font-semibold text-white text-lg">
            {isNew ? "Nou article" : `Editar · ${post.title?.ca || post.id}`}
          </h2>
          <button data-testid="blog-editor-close" onClick={onClose} className="p-2 text-white/50 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Slug + Tag + Cover + Published */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Slug (URL)</label>
              <input data-testid="blog-slug" value={slug} onChange={(e) => setSlug(slugify(e.target.value))}
                     disabled={!isNew} placeholder="ex: beneficis-ia-comerc-local" className={`${inputCls} mt-1.5 disabled:opacity-60`} />
              <p className="mt-1 text-[11px] text-white/30">S&apos;usa a l&apos;URL: /blog/{slug || "..."}</p>
            </div>
            <div>
              <label className={labelCls}>Etiqueta</label>
              <input data-testid="blog-tag" value={tag} onChange={(e) => setTag(e.target.value)}
                     placeholder="Tecnologia" className={`${inputCls} mt-1.5`} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Imatge de portada</label>
            <div className="mt-1.5 grid sm:grid-cols-[1fr_auto] gap-3 items-start">
              <input data-testid="blog-cover" value={cover} onChange={(e) => setCover(e.target.value)}
                     placeholder="URL de la imatge o puja'n una" className={inputCls} />
              <button type="button" data-testid="blog-upload-btn" onClick={() => fileInput.current?.click()} disabled={uploading}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/80 hover:border-neon/50 hover:text-neon disabled:opacity-50">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Pujant...</> : <><Upload className="w-4 h-4" /> Puja imatge</>}
              </button>
              <input ref={fileInput} type="file" accept="image/*" hidden onChange={onFile} data-testid="blog-file-input" />
            </div>
            {cover && (
              <img src={cover} alt="cover preview" className="mt-3 h-28 w-auto rounded-lg border border-white/10 object-cover" />
            )}
          </div>

          {/* Language tabs */}
          <div>
            <div className="inline-flex rounded-full border border-white/10 bg-black p-1">
              {LANGS.map((lg) => (
                <button key={lg.key} data-testid={`blog-lang-${lg.key}`} type="button" onClick={() => setActiveLang(lg.key)}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeLang === lg.key ? "bg-neon text-black" : "text-white/60 hover:text-white"}`}>
                  {lg.label}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className={labelCls}>Títol ({activeLang.toUpperCase()})</label>
                <input data-testid={`blog-title-${activeLang}`} value={title[activeLang]}
                       onChange={(e) => onTitleChange(e.target.value)} className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label className={labelCls}>Extracte curt ({activeLang.toUpperCase()})</label>
                <textarea data-testid={`blog-excerpt-${activeLang}`} value={excerpt[activeLang]} rows={2}
                          onChange={(e) => setExcerpt({ ...excerpt, [activeLang]: e.target.value })}
                          className={`${inputCls} mt-1.5 resize-none`} />
              </div>
              <div>
                <label className={labelCls}>Cos de l&apos;article ({activeLang.toUpperCase()}) — Markdown compatible</label>
                <textarea data-testid={`blog-body-${activeLang}`} value={body[activeLang]} rows={14}
                          onChange={(e) => setBody({ ...body, [activeLang]: e.target.value })}
                          placeholder={"## Un títol\n\nEl teu contingut aquí. Pots fer servir **negreta**, *cursiva*, llistes i enllaços."}
                          className={`${inputCls} mt-1.5 font-mono text-[13px] leading-relaxed`} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur rounded-b-2xl">
          <button data-testid="blog-toggle-published" onClick={() => setPublished(!published)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${published ? "border-neon/50 text-neon" : "border-white/15 text-white/60 hover:text-white"}`}>
            {published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {published ? "Publicat" : "Esborrany"}
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white">
              Cancel·la
            </button>
            <button data-testid="blog-save-btn" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-neon px-5 py-2.5 text-sm font-semibold text-black hover:bg-white disabled:opacity-60">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardant...</> : <><Save className="w-4 h-4" /> Guarda</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
